/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from 'react';
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";
import { MediaPowderService } from "../../../../../service/MediaPowderServices";
import MediaPowderSpecifications  from "../views/mediaPowderSpecifications";
import GptAuditSection  from "../views/gptAuditSection";
import LotDetails  from "../views/lotDetails";


interface MediaLotDetailsProps {
  lotId: string;
  selectedLotSummary?: any;
  onBack: () => void;
  onViewBatchDetails?: (batchId: string) => void;
  onViewLotUsage?: (lotId: string) => void;
}
const MediaLotDetails: React.FC<MediaLotDetailsProps> = ({ lotId, selectedLotSummary, onBack, onViewBatchDetails, onViewLotUsage }) => {
  const { t } = useTranslation();
  const [lotList, setLotList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [batchInfo, setBatchInfo] = useState<any>(null);

  useEffect(() => {
    const fetchLotDetails = async () => {
      setLoading(true);
      try {
        const res = await MediaPowderService.getMediaPowderLotDetails(lotId);
        if (res?.status) {
          setLotList(res.data || []);
          setBatchInfo(res.batch_info);
        }
      } catch (err) {
        console.error("Error fetching lot details", err);
      } finally {
        setLoading(false);
      }
    };

    if (lotId) fetchLotDetails();
  }, [lotId]);

  const lotData = lotList[0];
  const allGptTests = lotList.flatMap((lot) => lot.gpt_tests || []);
  const latestGptTest = allGptTests.sort((a, b) => new Date(b.gpt_test_date).getTime() - new Date(a.gpt_test_date).getTime())[0];
  const lotWithLatestGpt = lotList.find((lot) =>lot.gpt_tests?.some((test: any) => test === latestGptTest));
  
  const lifecycleEvents = [
    { date: batchInfo?.manufacture_date?.split(" ")[0] || "-",
      event: t('mediaPowder.lotDetails.events.manufactured'),
      user: t('mediaPowder.lotDetails.system'),
      status: t('mediaPowder.lotDetails.completed') },
    { date: batchInfo?.created_at?.split(" ")[0] || "-", 
      event: t('mediaPowder.lotDetails.events.received'), 
      user: t('mediaPowder.lotDetails.admin'), 
      status: t('mediaPowder.lotDetails.completed') },
    { date: latestGptTest?.gpt_test_date?.split(" ")[0] || t('mediaPowder.lotDetails.pending'),
      event: t('mediaPowder.lotDetails.events.qcInitiated'),
      user: t('mediaPowder.lotDetails.labTech'),
      status: latestGptTest ? t('mediaPowder.lotDetails.completed') : t('mediaPowder.lotDetails.active')
    },
    { date: latestGptTest
        ? latestGptTest.approved_on
          ? latestGptTest.approved_on.split(" ")[0]
          : latestGptTest.qa_status
        : "PENDING",
      event: t('mediaPowder.lotDetails.events.qcApproved'),
      user: t('mediaPowder.lotDetails.qaManager'),
      status: latestGptTest? latestGptTest.approved_on? t('mediaPowder.lotDetails.completed') : t('mediaPowder.lotDetails.active') : t('mediaPowder.lotDetails.active')},
    {
      date: lotWithLatestGpt?.opened_date || t('mediaPowder.lotDetails.notOpened'),
      event: t('mediaPowder.lotDetails.events.opened'),
      user: t('mediaPowder.lotDetails.system'),
      status:
        lotWithLatestGpt?.opened_date &&
        lotWithLatestGpt.opened_date !== t('mediaPowder.lotDetails.notOpened')
          ? t('mediaPowder.lotDetails.completed')
          : t('mediaPowder.lotDetails.active')
    }
  ];

  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-0">
    <MediaPrepHeaderSection />
      <MediaPrepPageHeader pageHeader={t('mediaPowder.lotDetails.pageTitle')}  />
      {/* Sticky Header */}
      <div className="sticky top-[110px] z-40 bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all">
            <span className="material-symbols-outlined text-xl">{t('mediaPowder.icons.arrow_back')}</span>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase flex items-center gap-3">
                {t('mediaPowder.lotDetails.pageTitle')}:
                <span className="px-3 py-[5px] bg-[#F1F5F9] text-[#334155] text-[16px] font-bold rounded-md border border-slate-200">
                  {batchInfo?.batch_name}
                </span>
              </h1>
              <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase rounded-full border border-emerald-200">
                {selectedLotSummary?.stockStatus}
              </span>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {selectedLotSummary?.name} • {selectedLotSummary?.mf}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-[#2D3E50] text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:brightness-110 shadow-sm transition-all">
            {t('mediaPowder.lotDetails.printLabel')}
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 py-10">
        {/* Top Header Section: Batch Life Cycle & Details */}
        <MediaPowderSpecifications
          lotData={lotData}
          selectedLotSummary={selectedLotSummary}
          lifecycleEvents={lifecycleEvents}
          batchInfo={batchInfo}
          t={t}
        />
        {/* GPT Performance Audit Section */}
        <GptAuditSection
          gptTest={latestGptTest}
          t={t}
        />
        {/* Lot Details */}
        <LotDetails
          lotList={lotList}
          onViewBatchDetails={onViewBatchDetails}
          onViewLotUsage={onViewLotUsage}
          t={t}
        />
      </div>
    </div>
  );
};

export default MediaLotDetails;
